/**
 * Sandbox Simulator - Provides ephemeral mock data for tour mode
 * No API calls - all data exists only in memory during tour
 */

import i18next from "i18next";

const TOUR_SESSION_ID = "tour:build-first-agent";

export type TourPromptKey =
  | "dailyEmail"
  | "weeklyReports"
  | "monitorAlerts"
  | "processDocs"
  | "collectData";

const TOUR_PROMPT_KEYS: TourPromptKey[] = [
  "dailyEmail",
  "weeklyReports",
  "monitorAlerts",
  "processDocs",
  "collectData",
];

const KEY_TO_BASE_PROMPT: Record<TourPromptKey, string> = {
  dailyEmail: "Create a workflow that sends a daily email summary",
  weeklyReports: "Build a workflow to automatically generate weekly reports",
  monitorAlerts: "Create an agent that monitors data and sends alerts",
  processDocs: "Build a workflow that processes documents and sends summaries",
  collectData:
    "Create a workflow that collects data from multiple sources and combines them",
};

function tourT(key: string, opts?: Record<string, unknown>) {
  return i18next.t(key, { ns: "settings", ...opts });
}

/** Load tour namespaces for current locale before reading tour strings */
export async function ensureTourI18nReady(): Promise<void> {
  await i18next.loadNamespaces(["settings", "chat"]);
}

export function getRandomTourPromptKey(): TourPromptKey {
  const randomIndex = Math.floor(Math.random() * TOUR_PROMPT_KEYS.length);
  return TOUR_PROMPT_KEYS[randomIndex] ?? "dailyEmail";
}

export function getTourPromptText(key: TourPromptKey): string {
  return tourT(`tour.prompts.${key}`);
}

function getTourPromptKeyFromText(prompt: string): TourPromptKey {
  const normalized = prompt.trim();
  for (const key of TOUR_PROMPT_KEYS) {
    if (getTourPromptText(key) === normalized) return key;
    if (KEY_TO_BASE_PROMPT[key] === normalized) return key;
  }
  return "dailyEmail";
}

function localizeWorkflow(
  base: MockWorkflow,
  key: TourPromptKey,
): MockWorkflow {
  const localized = tourT(`tour.workflows.${key}`, {
    returnObjects: true,
  }) as {
    name?: string;
    description?: string;
    steps?: Record<string, { name?: string; description?: string }>;
  };

  return {
    ...base,
    name: localized?.name ?? base.name,
    description: localized?.description ?? base.description,
    steps: base.steps.map((step, index) => {
      const stepLoc = localized?.steps?.[String(index)];
      return {
        ...step,
        name: stepLoc?.name ?? step.name,
        description: stepLoc?.description ?? step.description,
      };
    }),
  };
}

export function getMockWorkflowForKey(key: TourPromptKey): MockWorkflow | null {
  const basePrompt = KEY_TO_BASE_PROMPT[key];
  const base = TOUR_MOCK_WORKFLOWS[basePrompt];
  if (!base) return null;
  return localizeWorkflow(base, key);
}

export interface MockWorkflow {
  id: string;
  name: string;
  description: string;
  cron_schedule: string | null;
  envs: any[];
  pip_packages: any[];
  inputs: any[];
  start_at: string[];
  steps: Array<{
    id: string;
    name: string;
    description: string;
    agent_prompt: string | null;
    services: string[];
    is_human_input_step: boolean | null;
    human_input_prompt: string | null;
    depends_on: string[];
    next_steps: Array<{
      step_id: string;
      condition: any;
    }> | null;
  }>;
}

/**
 * Mock workflow responses for tour prompts
 */
const TOUR_MOCK_WORKFLOWS: Record<string, MockWorkflow> = {
  // Basic workflow: daily email summary
  "Create a workflow that sends a daily email summary": {
    id: "tour-workflow-1",
    name: "Daily Email Summary",
    description: "Sends a daily email summary",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Generate Summary",
        description: "Generate daily summary content",
        agent_prompt: "Generate a daily summary of activities",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Send Email",
        description: "Send the summary via email",
        agent_prompt: "Send the summary via email",
        services: [],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_1"],
        next_steps: null,
      },
    ],
  },
  // With Gmail integration
  "Add Gmail integration to send the email": {
    id: "tour-workflow-1-gmail",
    name: "Daily Email Summary",
    description: "Sends a daily email summary using Gmail",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Generate Summary",
        description: "Generate daily summary content",
        agent_prompt: "Generate a daily summary of activities",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Send Email via Gmail",
        description: "Send the summary via Gmail",
        agent_prompt: "Send the summary via Gmail",
        services: ["gmail:send_email"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_1"],
        next_steps: null,
      },
    ],
  },
  // With human approval
  "Add a human approval step before sending the email": {
    id: "tour-workflow-1-approval",
    name: "Daily Email Summary with Approval",
    description: "Sends a daily email summary after human approval",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Generate Summary",
        description: "Generate daily summary content",
        agent_prompt: "Generate a daily summary of activities",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Human Approval",
        description: "Wait for human approval before sending",
        agent_prompt: null,
        services: [],
        is_human_input_step: true,
        human_input_prompt:
          "Please review and approve the email summary before sending",
        depends_on: ["step_1"],
        next_steps: [{ step_id: "step_3", condition: null }],
      },
      {
        id: "step_3",
        name: "Send Email via Gmail",
        description: "Send the approved summary via Gmail",
        agent_prompt: "Send the summary via Gmail",
        services: ["gmail:send_email"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_2"],
        next_steps: null,
      },
    ],
  },
  // Weekly reports workflow
  "Build a workflow to automatically generate weekly reports": {
    id: "tour-workflow-weekly-reports",
    name: "Weekly Reports Generator",
    description: "Automatically generates weekly reports",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Collect Data",
        description: "Collect data from various sources",
        agent_prompt: "Collect weekly data from sources",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Generate Report",
        description: "Generate weekly report",
        agent_prompt: "Generate weekly report from collected data",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_1"],
        next_steps: null,
      },
    ],
  },
  // Monitoring and alerts workflow
  "Create an agent that monitors data and sends alerts": {
    id: "tour-workflow-monitor-alerts",
    name: "Data Monitor and Alerts",
    description: "Monitors data and sends alerts",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Monitor Data",
        description: "Continuously monitor data sources",
        agent_prompt: "Monitor data sources for changes",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Send Alert",
        description: "Send alert when conditions are met",
        agent_prompt: "Send alert notification",
        services: [],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_1"],
        next_steps: null,
      },
    ],
  },
  // Document processing workflow
  "Build a workflow that processes documents and sends summaries": {
    id: "tour-workflow-doc-process",
    name: "Document Processor",
    description: "Processes documents and sends summaries",
    cron_schedule: null,
    envs: [],
    pip_packages: [],
    inputs: [],
    start_at: ["step_1"],
    steps: [
      {
        id: "step_1",
        name: "Process Document",
        description: "Process incoming documents",
        agent_prompt: "Process and analyze document",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: [],
        next_steps: [{ step_id: "step_2", condition: null }],
      },
      {
        id: "step_2",
        name: "Generate Summary",
        description: "Generate summary of processed document",
        agent_prompt: "Generate document summary",
        services: ["call_ai_llm"],
        is_human_input_step: null,
        human_input_prompt: null,
        depends_on: ["step_1"],
        next_steps: null,
      },
    ],
  },
  // Data collection workflow
  "Create a workflow that collects data from multiple sources and combines them":
    {
      id: "tour-workflow-data-collect",
      name: "Multi-Source Data Collector",
      description: "Collects data from multiple sources and combines them",
      cron_schedule: null,
      envs: [],
      pip_packages: [],
      inputs: [],
      start_at: ["step_1"],
      steps: [
        {
          id: "step_1",
          name: "Collect from Source 1",
          description: "Collect data from first source",
          agent_prompt: "Collect data from source 1",
          services: [],
          is_human_input_step: null,
          human_input_prompt: null,
          depends_on: [],
          next_steps: [{ step_id: "step_2", condition: null }],
        },
        {
          id: "step_2",
          name: "Collect from Source 2",
          description: "Collect data from second source",
          agent_prompt: "Collect data from source 2",
          services: [],
          is_human_input_step: null,
          human_input_prompt: null,
          depends_on: ["step_1"],
          next_steps: [{ step_id: "step_3", condition: null }],
        },
        {
          id: "step_3",
          name: "Combine Data",
          description: "Combine data from all sources",
          agent_prompt: "Combine and merge data from all sources",
          services: ["call_ai_llm"],
          is_human_input_step: null,
          human_input_prompt: null,
          depends_on: ["step_2"],
          next_steps: null,
        },
      ],
    },
};

/**
 * Get workflow lifecycle explanation response
 */
export function getWorkflowLifecycleResponse(prompt: string): string {
  return tourT("tour.lifecycleResponse", { prompt });
}

/**
 * Activate tour mode
 */
export function activateTourMode(): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem("tour:active", "true");
  sessionStorage.setItem("tour:sessionId", TOUR_SESSION_ID);
}

/**
 * Deactivate tour mode
 */
export function deactivateTourMode(): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem("tour:active");
  sessionStorage.removeItem("tour:sessionId");
}

/**
 * Get mock workflow for a prompt
 */
export function getMockWorkflow(prompt: string): MockWorkflow | null {
  const key = getTourPromptKeyFromText(prompt);
  const fromKey = getMockWorkflowForKey(key);
  if (fromKey) return fromKey;

  const normalizedPrompt = prompt.trim();
  if (TOUR_MOCK_WORKFLOWS[normalizedPrompt]) {
    return localizeWorkflow(TOUR_MOCK_WORKFLOWS[normalizedPrompt]!, key);
  }

  if (
    normalizedPrompt.toLowerCase().includes("email") ||
    normalizedPrompt.toLowerCase().includes("summary") ||
    normalizedPrompt.includes("メール") ||
    normalizedPrompt.includes("サマリー")
  ) {
    return getMockWorkflowForKey("dailyEmail");
  }

  return getMockWorkflowForKey("dailyEmail");
}

/**
 * Get mock chat response for a prompt (for final workflow response after planning)
 */
export function getMockResponse(prompt: string): string {
  const key = getTourPromptKeyFromText(prompt);
  const specific = tourT(`tour.mockResponses.${key}`, { defaultValue: "" });
  if (typeof specific === "string" && specific.length > 0) {
    return specific;
  }
  return tourT("tour.mockResponse");
}

/**
 * Create mock messages for tour with realistic planning phase simulation
 */
export function createMockMessages(
  userPrompt: string,
  workflow: MockWorkflow | null,
  includePlanning: boolean = true,
): Array<{
  id: string;
  content: string;
  role: "user" | "assistant" | "system";
  timestamp: string;
  isStreaming?: boolean;
  toDos?: Array<{
    id: string;
    text: string;
    status: "started" | "completed" | "failed";
  }>;
  workflow?: MockWorkflow;
}> {
  const messages: Array<{
    id: string;
    content: string;
    role: "user" | "assistant" | "system";
    timestamp: string;
    isStreaming?: boolean;
    toDos?: Array<{
      id: string;
      text: string;
      status: "started" | "completed" | "failed";
    }>;
    workflow?: MockWorkflow;
  }> = [
    {
      id: `tour-msg-user-${Date.now()}`,
      content: userPrompt,
      role: "user" as const,
      timestamp: new Date().toISOString(),
    },
  ];

  if (includePlanning && workflow) {
    // Create assistant message with planning to-dos
    const assistantMessage = {
      id: `tour-msg-assistant-${Date.now()}`,
      content: "",
      role: "assistant" as const,
      timestamp: new Date(Date.now() + 500).toISOString(),
      isStreaming: true,
      toDos: [
        {
          id: "todo-1",
          text: tourT("tour.todos.0"),
          status: "completed" as const,
        },
        {
          id: "todo-2",
          text: tourT("tour.todos.1"),
          status: "completed" as const,
        },
        {
          id: "todo-3",
          text: tourT("tour.todos.2"),
          status: "completed" as const,
        },
        {
          id: "todo-4",
          text: tourT("tour.todos.3"),
          status: "completed" as const,
        },
      ],
    };
    messages.push(assistantMessage);

    // Final assistant message with workflow
    messages.push({
      id: `tour-msg-assistant-final-${Date.now() + 1000}`,
      content: getMockResponse(userPrompt),
      role: "assistant" as const,
      timestamp: new Date(Date.now() + 2000).toISOString(),
      isStreaming: false,
      workflow: workflow,
    });
  } else {
    messages.push({
      id: `tour-msg-assistant-${Date.now()}`,
      content: getMockResponse(userPrompt),
      role: "assistant" as const,
      timestamp: new Date(Date.now() + 1000).toISOString(),
      ...(workflow ? { workflow } : {}),
    });
  }

  return messages;
}

/**
 * Simulate planning phase with progressive to-dos
 */
export async function simulatePlanningPhase(
  onUpdateThinking: (text: string | null) => void,
  onAddTodo: (todo: {
    id: string;
    text: string;
    status: "started" | "completed" | "failed";
  }) => void,
  onCompleteTodo: (id: string) => void,
): Promise<void> {
  const thinkingMessages = [
    tourT("tour.thinking.0"),
    tourT("tour.thinking.1"),
    tourT("tour.thinking.2"),
    tourT("tour.thinking.3"),
  ];

  const todos = [
    { id: "todo-1", text: tourT("tour.todos.0") },
    { id: "todo-2", text: tourT("tour.todos.1") },
    { id: "todo-3", text: tourT("tour.todos.2") },
    { id: "todo-4", text: tourT("tour.todos.3") },
  ];

  for (let i = 0; i < thinkingMessages.length; i++) {
    onUpdateThinking(thinkingMessages[i]!);
    onAddTodo({ ...todos[i]!, status: "started" });
    await new Promise((resolve) => setTimeout(resolve, 800));
    onCompleteTodo(todos[i]!.id);
    await new Promise((resolve) => setTimeout(resolve, 300));
  }

  onUpdateThinking(null);
}
